/*
 * Lumos cart operation coordination.
 *
 * This file deliberately owns no DOM controller and never starts a request by
 * itself. ProductVariants and CartSection call the small hooks below around
 * their existing requests. That keeps regional form serialization and cart
 * markup in the host theme while giving every host the same lifecycle rules.
 */
(function (global) {
  'use strict';

  if (global.LumosCartOperations) return;

  var documentElement = global.document;
  var state = {
    productOperations: [],
    productOwners: [],
    productEvents: [],
    cartOperation: null,
    generation: 0
  };

  function clone(value) {
    if (value === undefined || value === null) return value;
    if (typeof global.structuredClone === 'function') {
      try { return global.structuredClone(value); } catch (error) { /* JSON fallback */ }
    }
    if (typeof value !== 'object') return value;
    try { return JSON.parse(JSON.stringify(value)); } catch (error) { return value; }
  }

  function dataKey(name) {
    return name.replace(/-([a-z])/g, function (_, letter) { return letter.toUpperCase(); });
  }

  function setData(element, name, value) {
    if (!element) return;
    if (element.dataset) element.dataset[dataKey(name)] = String(value);
    else if (element.setAttribute) element.setAttribute('data-' + name, String(value));
  }

  function getData(element, name) {
    if (!element) return undefined;
    if (element.dataset && element.dataset[dataKey(name)] !== undefined) return element.dataset[dataKey(name)];
    return element.getAttribute ? element.getAttribute('data-' + name) : undefined;
  }

  function deleteData(element, name) {
    if (!element) return;
    if (element.dataset) delete element.dataset[dataKey(name)];
    if (element.removeAttribute) element.removeAttribute('data-' + name);
  }

  function isConnected(element) {
    return !element || element.isConnected === undefined || element.isConnected === true;
  }

  function isAlive(owner) {
    return !!owner && owner._lumosCartUnloaded !== true && isConnected(owner.element);
  }

  function customEvent(name, detail) {
    if (typeof global.CustomEvent === 'function') return new global.CustomEvent(name, { bubbles: true, detail: detail });
    if (documentElement && documentElement.createEvent) {
      var event = documentElement.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, detail);
      return event;
    }
    return { type: name, bubbles: true, detail: detail };
  }

  function dispatch(owner, name, detail) {
    var target = owner && owner.element ? owner.element : documentElement;
    if (target && target.dispatchEvent) target.dispatchEvent(customEvent(name, detail));
  }

  function indexOf(items, value) {
    for (var i = 0; i < items.length; i += 1) if (items[i] === value) return i;
    return -1;
  }

  function remove(items, value) {
    var index = indexOf(items, value);
    if (index >= 0) items.splice(index, 1);
  }

  function registerProductOwner(owner) {
    if (indexOf(state.productOwners, owner) < 0) state.productOwners.push(owner);
    owner._lumosCartUnloaded = false;
  }

  function registerCartSection(owner) {
    if (!owner) return;
    owner._lumosCartUnloaded = false;
  }

  function ownerForm(owner) {
    if (!owner) return null;
    if (owner._lumosCartForm && isConnected(owner._lumosCartForm)) return owner._lumosCartForm;
    if (!owner.element || !owner.element.querySelector) return null;
    return owner.element.querySelector('form[action*="/cart/add"]');
  }

  function formSnapshot(form) {
    var snapshot = {
      action: form && form.getAttribute ? form.getAttribute('action') : undefined,
      method: form && form.getAttribute ? form.getAttribute('method') : undefined,
      fields: []
    };
    var elements = form && form.elements;
    if (!elements) return snapshot;
    for (var i = 0; i < elements.length; i += 1) {
      var field = elements[i];
      if (!field || !field.name || field.disabled) continue;
      var type = String(field.type || '').toLowerCase();
      if ((type === 'checkbox' || type === 'radio') && !field.checked) continue;
      snapshot.fields.push({ name: field.name, value: field.value === undefined ? '' : String(field.value) });
    }
    return snapshot;
  }

  function numberOr(value, fallback) {
    var parsed = Number(value);
    return isFinite(parsed) ? parsed : fallback;
  }

  function requestQuantity(request, fallback, form) {
    if (request && Array.isArray(request.items) && request.items.length) return numberOr(request.items[0].quantity, fallback || 1);
    if (request && request.quantity !== undefined) return numberOr(request.quantity, fallback || 1);
    var quantity = form && form.querySelector ? form.querySelector('[name="quantity"]') : null;
    return numberOr(quantity && quantity.value, fallback || 1);
  }

  function sameVariant(left, right) {
    if (left === right) return true;
    if (!left || !right || left.id === undefined || right.id === undefined) return false;
    return String(left.id) === String(right.id);
  }

  function requiredOptionsComplete(owner, form) {
    if (!form || !form.querySelectorAll) return true;
    var root = owner && owner.element ? owner.element : form;
    var cursor = root;
    while (cursor) {
      if (cursor.classList && (cursor.classList.contains('is-ultra-selection-incomplete') || cursor.classList.contains('is-ultra-mobile-selection-incomplete'))) return false;
      cursor = cursor.parentNode;
    }
    var incomplete = root.querySelectorAll('.is-ultra-selection-incomplete, .is-ultra-mobile-selection-incomplete');
    if (incomplete.length) return false;
    return true;
  }

  function selectedOption(select) {
    if (select.options && select.selectedIndex >= 0) return select.options[select.selectedIndex] || null;
    return select.querySelector ? select.querySelector('option[selected]') : null;
  }

  /* Legacy bundle selectors submit a separate bundle product by rewriting the
   * selected master option value. Two server-rendered shapes exist: "Select A
   * Bundle" radios (option-bundle) and helmet kit radios inside the Helmet Type
   * option (Winter Kit, All-Weather Kit). A kit value is not a native option
   * value, so the host has no current variant for it. Accept the rewritten ID
   * only when the master option still records a native variant and the checked
   * radio renders the same, non-disabled ID in its own bundle select. Firefly
   * contract forms keep their own guard. */
  function legacyBundleOption(form, master, variant) {
    if (!form || !master || !form.querySelectorAll || form.querySelector('[data-lumos-firefly-contract]')) return null;
    var option = selectedOption(master);
    var original = option && option.getAttribute ? option.getAttribute('data-value-original') : null;
    if (!original || (variant && original !== String(variant.id))) return null;
    var submitted = String(master.value);
    if (!/^[1-9]\d*$/.test(submitted) || submitted === original) return null;
    var choices = form.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < choices.length; i += 1) {
      var scope = choices[i].parentNode;
      var selects = scope && scope.querySelectorAll ? scope.querySelectorAll('select[name="bundle-select"]') : [];
      for (var j = 0; j < selects.length; j += 1) {
        var bundleOptions = selects[j].querySelectorAll('option');
        for (var k = 0; k < bundleOptions.length; k += 1) {
          if (String(bundleOptions[k].value) === submitted) return bundleOptions[k];
        }
      }
    }
    return null;
  }

  function optionDisabled(option) {
    return !!option.disabled || (option.getAttribute && option.getAttribute('disabled') !== null);
  }

  function nativeSelectionReady(owner, form, variant) {
    if (!owner || !form) return false;
    var master = owner.masterSelector || (form.querySelector && form.querySelector('select[name="id"], input[name="id"]'));
    if (!variant) {
      var kit = legacyBundleOption(form, master, null);
      return !!kit && !optionDisabled(kit) && requiredOptionsComplete(owner, form);
    }
    if (owner.currentVariant && !sameVariant(owner.currentVariant, variant)) return false;
    if (master && master.value !== undefined && (variant.id === undefined || String(master.value) !== String(variant.id))) {
      var bundle = legacyBundleOption(form, master, variant);
      if (!bundle || optionDisabled(bundle)) return false;
    }
    if (variant.available === false) return false;
    return requiredOptionsComplete(owner, form);
  }

  function canSubmit(owner, form, variant) {
    return nativeSelectionReady(owner, form, variant) && purchaseTarget(owner, form, variant) !== null;
  }

  /* A purchase target never replaces the native variant or master selector.
   * Only a form with a server-rendered contract may submit a different ID. */
  function purchaseTarget(owner, form, variant) {
    var contract = form && form.querySelector && form.querySelector('[data-lumos-firefly-contract]');
    if (!contract) return undefined;
    var adapter = global.LumosFireflyPurchase;
    if (!adapter || typeof adapter.resolve !== 'function') return null;
    var target = adapter.resolve(owner, form, variant);
    if (!target || !variant || String(target.nativeVariantId) !== String(variant.id) || !/^[1-9]\d*$/.test(String(target.id)) || target.available !== true) return null;
    return target;
  }

  function requestMatchesTarget(request, target) {
    if (target === undefined) return true;
    if (!target || !request) return false;
    var item = Array.isArray(request.items) ? request.items[0] : request;
    return !!item && String(item.id) === String(target.id);
  }

  function refreshProductAdapters() {
    var adapter = global.LumosFireflyPurchase;
    if (!adapter) return;
    state.productOwners.forEach(function (owner) {
      if (isAlive(owner)) adapter.bind(owner, ownerForm(owner));
    });
    syncProductOwners();
  }

  function hasPendingProductForForm(form) {
    for (var i = 0; i < state.productOperations.length; i += 1) {
      if (state.productOperations[i].form === form && !state.productOperations[i].finished) return true;
    }
    return false;
  }

  function languageValue(keys, fallback) {
    var languages = global.languages || {};
    for (var i = 0; i < keys.length; i += 1) {
      if (languages[keys[i]]) return String(languages[keys[i]]);
    }
    return fallback;
  }

  function errorMessage(error, fallback) {
    if (error && error.code === 'LUMOS_CART_CONFLICT') {
      return languageValue(fallback === 'add' ? ['cartAddError', 'productFormAddError', 'unableToAddToCart'] : ['cartUpdateError', 'cartError', 'unableToUpdateCart'], 'Another cart operation is still in progress.');
    }
    if (error && error.content) {
      var content = error.content;
      if (content.description) return String(content.description);
      if (content.message) return String(content.message);
    }
    if (error && error.message && !/^Unable to (add to cart|update cart|refresh cart)\.$/.test(String(error.message))) return String(error.message);
    return languageValue(fallback === 'add' ? ['cartAddError', 'productFormAddError', 'unableToAddToCart'] : ['cartUpdateError', 'cartError', 'unableToUpdateCart'], fallback === 'add' ? 'Unable to add to cart.' : 'Unable to update cart.');
  }

  function removeScopedError(root, selector) {
    if (!root || !root.querySelectorAll) return;
    var nodes = root.querySelectorAll(selector);
    for (var i = nodes.length - 1; i >= 0; i -= 1) if (nodes[i] && nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
  }

  function showError(owner, error, kind, anchor) {
    var root = owner && owner.element;
    if (!root || !documentElement || !documentElement.createElement) return;
    var selector = kind === 'add' ? '.atc-error-message' : '.Cart__Error';
    removeScopedError(root, selector);
    var message = documentElement.createElement('div');
    message.className = kind === 'add' ? 'atc-error-message' : 'Cart__Error';
    message.setAttribute('role', 'alert');
    message.style.color = 'red';
    message.style.marginTop = '5px';
    message.textContent = errorMessage(error, kind);
    var target = anchor && anchor.parentNode ? anchor.parentNode : root;
    if (target && target.insertBefore) target.insertBefore(message, anchor && anchor.nextSibling ? anchor.nextSibling : null);
    else if (root.appendChild) root.appendChild(message);
  }

  function clearProductError(owner) {
    if (owner && owner.element) removeScopedError(owner.element, '.atc-error-message');
  }

  function clearCartError(owner) {
    if (owner && owner.element) removeScopedError(owner.element, '.Cart__Error');
  }

  function syncProductOwners() {
    for (var i = state.productOwners.length - 1; i >= 0; i -= 1) {
      var owner = state.productOwners[i];
      if (!isAlive(owner)) {
        if (!owner || owner._lumosCartUnloaded) state.productOwners.splice(i, 1);
        continue;
      }
      var button = owner.element && owner.element.querySelector ? owner.element.querySelector('.ProductForm__AddToCart') : null;
      var variant = owner.currentVariant;
      syncButton(owner, button, variant);
    }
  }

  function syncButton(owner, button, variant) {
    if (!button) return;
    var pending = false;
    for (var i = 0; i < state.productOperations.length; i += 1) {
      if (state.productOperations[i].owner === owner && !state.productOperations[i].finished) {
        pending = true;
        break;
      }
    }
    if (state.cartOperation || state.productOperations.length) pending = true;
    var ready = canSubmit(owner, ownerForm(owner), variant);
    var shouldDisable = pending || !ready;
    var shouldBusy = pending;

    /* Keep these states independent. An incomplete or unavailable selection
     * is disabled, but it is not busy. Also avoid writing an already-correct
     * attribute: the Ultra availability observer watches disabled and would
     * otherwise schedule another sync for every no-op write. */
    if (shouldDisable) {
      if (button.getAttribute('disabled') === null) button.setAttribute('disabled', 'disabled');
    } else if (button.getAttribute('disabled') !== null) {
      button.removeAttribute('disabled');
    }
    if (shouldBusy) {
      if (button.getAttribute('aria-busy') !== 'true') button.setAttribute('aria-busy', 'true');
    } else if (button.getAttribute('aria-busy') !== null) {
      button.removeAttribute('aria-busy');
    }
  }

  function syncProductForm(form) {
    if (!form) return 0;
    var matched = 0;
    for (var i = state.productOwners.length - 1; i >= 0; i -= 1) {
      var owner = state.productOwners[i];
      if (!isAlive(owner) || ownerForm(owner) !== form) continue;
      var button = owner.element && owner.element.querySelector ? owner.element.querySelector('.ProductForm__AddToCart') : null;
      syncButton(owner, button, owner.currentVariant);
      matched += 1;
    }
    return matched;
  }

  function findProductEvent(detail) {
    for (var i = state.productEvents.length - 1; i >= 0; i -= 1) {
      if (state.productEvents[i].detail === detail) return state.productEvents[i].operation;
    }
    return null;
  }

  function rememberProductEvent(detail, operation) {
    state.productEvents.push({ detail: detail, operation: operation });
    if (state.productEvents.length > 32) state.productEvents.shift();
  }

  function conflictError(message) {
    var error = new Error(message || 'Another cart operation is still in progress.');
    error.code = 'LUMOS_CART_CONFLICT';
    return error;
  }

  function bindProductForm(owner, form) {
    if (!owner || !form || !form.addEventListener) return;
    registerProductOwner(owner);
    if (owner._lumosCartForm === form && owner._lumosCartSubmitListener) return;
    unbindProductForm(owner);
    owner._lumosCartForm = form;
    owner._lumosCartSubmitListener = function (event) {
      if (!event || event.target !== form || event.__lumosCartOperationsHandled) return;
      if (!owner.options || owner.options['useAjaxCart'] === false) return;
      event.preventDefault();
      event.__lumosCartOperationsHandled = true;
      if (typeof owner._addToCart === 'function') owner._addToCart(event);
    };
    setData(form, 'lumosCartManaged', 'true');
    form.addEventListener('submit', owner._lumosCartSubmitListener);
    if (global.LumosFireflyPurchase) global.LumosFireflyPurchase.bind(owner, form);
  }

  function unbindProductForm(owner) {
    if (!owner) return;
    if (global.LumosFireflyPurchase) global.LumosFireflyPurchase.unbind(owner);
    var form = owner._lumosCartForm;
    if (form && owner._lumosCartSubmitListener && form.removeEventListener) form.removeEventListener('submit', owner._lumosCartSubmitListener);
    if (form) deleteData(form, 'lumosCartManaged');
    delete owner._lumosCartForm;
    delete owner._lumosCartSubmitListener;
  }

  function beginProductAdd(owner, options) {
    options = options || {};
    var form = options.form || ownerForm(owner);
    var variant = options.variant || (owner && owner.currentVariant);
    if (!nativeSelectionReady(owner, form, variant) || hasPendingProductForForm(form) || state.productOperations.length || state.cartOperation) return null;
    var target = purchaseTarget(owner, form, variant);
    if (!requestMatchesTarget(options.request, target)) return null;
    var operation = {
      type: 'product',
      owner: owner,
      form: form,
      button: options.button,
      variant: clone(variant),
      purchaseTarget: clone(target),
      quantity: requestQuantity(options.request, options.quantity, form),
      request: clone(options.request),
      formSnapshot: formSnapshot(form),
      finished: false
    };
    state.productOperations.push(operation);
    setData(form, 'lumosCartPending', 'true');
    if (options.button) {
      options.button.setAttribute('disabled', 'disabled');
      options.button.setAttribute('aria-busy', 'true');
    }
    dispatch(owner, 'product:state', { variant: clone(variant), pending: true, form: form });
    return operation;
  }

  function finishProductAdd(operation, options) {
    if (!operation || operation.finished) return null;
    options = options || {};
    var owner = operation.owner;
    var successful = options.ok === true;
    var detail;
    if (successful) {
      clearProductError(owner);
      detail = {
        variant: clone(operation.variant),
        quantity: operation.quantity,
        form: operation.form,
        request: clone(operation.request)
      };
      if (operation.purchaseTarget) detail.purchaseTarget = clone(operation.purchaseTarget);
      rememberProductEvent(detail, operation);
      if (isAlive(owner)) dispatch(owner, 'product:added', detail);
    } else {
      var error = options.error instanceof Error ? options.error : new Error(errorMessage(options.error, 'add'));
      showError(owner, error, 'add', operation.button);
      detail = { error: error, form: operation.form, request: clone(operation.request) };
      if (isAlive(owner)) dispatch(owner, 'product:add-error', detail);
    }
    operation.finished = true;
    remove(state.productOperations, operation);
    if (!hasPendingProductForForm(operation.form)) deleteData(operation.form, 'lumosCartPending');
    dispatch(owner, 'product:state', { variant: clone(owner && owner.currentVariant || operation.variant), pending: false, form: operation.form });
    syncProductOwners();
    return detail;
  }

  function beginCartMutation(owner, context) {
    if (!owner || !isAlive(owner) || state.cartOperation || state.productOperations.length) return null;
    var operation = {
      type: 'cart',
      kind: 'mutation',
      owner: owner,
      context: context,
      generation: ++state.generation,
      getController: null,
      needsRefresh: false,
      invalidated: false,
      finished: false
    };
    state.cartOperation = operation;
    syncProductOwners();
    return operation;
  }

  function beginCartRender(owner, context) {
    if (!owner || !isAlive(owner)) return null;
    if (state.cartOperation) {
      if (context && context.type === 'cart' && state.cartOperation === context) return context;
      if (state.cartOperation.kind === 'mutation') {
        /* A POST owns the cart until its response and render settle. Remember
         * an external refresh request, but never replay the POST. The host
         * can perform its normal follow-up render after the mutation. */
        state.cartOperation.needsRefresh = true;
        return null;
      }
      /* A later GET-only refresh supersedes an older one. Invalidate and
       * abort the old request before opening a fresh generation so a late
       * response cannot replace newer cart HTML. */
      var previous = state.cartOperation;
      previous.invalidated = true;
      if (previous.getController && previous.getController.abort) previous.getController.abort();
      finishCartOperation(previous);
    }
    var linkedProduct = findProductEvent(context);
    if (state.productOperations.length && !linkedProduct) return null;
    var operation = {
      type: 'cart',
      kind: 'render',
      owner: owner,
      context: context,
      linkedProduct: linkedProduct,
      generation: ++state.generation,
      getController: null,
      invalidated: false,
      finished: false
    };
    state.cartOperation = operation;
    syncProductOwners();
    return operation;
  }

  function attachCartGet(operation, controller) {
    if (!operation) return;
    operation.getController = controller || null;
    if (operation.invalidated && controller && controller.abort) controller.abort();
  }

  function isCurrentCartOperation(operation) {
    return !!operation && !operation.finished && !operation.invalidated && state.cartOperation === operation && operation.generation === state.generation && isAlive(operation.owner);
  }

  function finishCartOperation(operation) {
    if (!operation || operation.finished) return;
    operation.finished = true;
    if (state.cartOperation === operation) state.cartOperation = null;
    syncProductOwners();
  }

  function failCartOperation(operation) {
    finishCartOperation(operation);
  }

  function consumeCartRefresh(operation) {
    if (!operation || operation.kind !== 'mutation') return false;
    var needed = operation.needsRefresh === true;
    operation.needsRefresh = false;
    return needed;
  }

  function unloadProductOwner(owner) {
    if (!owner) return;
    owner._lumosCartUnloaded = true;
    unbindProductForm(owner);
    for (var i = state.productOwners.length - 1; i >= 0; i -= 1) if (state.productOwners[i] === owner) state.productOwners.splice(i, 1);
    /* A POST is intentionally not aborted. Its completion still releases the form lock. */
    syncProductOwners();
  }

  function unloadCartSection(owner) {
    if (!owner) return;
    owner._lumosCartUnloaded = true;
    var operation = state.cartOperation;
    if (!operation || operation.owner !== owner) return;
    state.generation += 1;
    operation.invalidated = true;
    if (operation.getController && operation.getController.abort) operation.getController.abort();
    /* Keep a mutation lock until its POST settles; only GET renders are released now. */
    if (operation.kind === 'render') finishCartOperation(operation);
  }

  var api = {
    bindProductForm: bindProductForm,
    refreshProductAdapters: refreshProductAdapters,
    unbindProductForm: unbindProductForm,
    bindProductVariants: registerProductOwner,
    unbindProductVariants: unloadProductOwner,
    bindCartSection: registerCartSection,
    unloadCartSection: unloadCartSection,
    beginProductAdd: beginProductAdd,
    finishProductAdd: finishProductAdd,
    beginCartMutation: beginCartMutation,
    beginCartRender: beginCartRender,
    attachCartGet: attachCartGet,
    isCurrentCartOperation: isCurrentCartOperation,
    finishCartOperation: finishCartOperation,
    failCartOperation: failCartOperation,
    consumeCartRefresh: consumeCartRefresh,
    showProductError: function (owner, error, button) { showError(owner, error, 'add', button); },
    showCartError: function (owner, error, anchor) { showError(owner, error, 'cart', anchor); },
    clearCartError: clearCartError,
    clearProductError: clearProductError,
    syncProductButton: syncButton,
    syncProductForm: syncProductForm,
    isProductReady: canSubmit,
    conflictError: conflictError,
    isFormComplete: requiredOptionsComplete,
    getState: function () {
      return {
        productPending: state.productOperations.length,
        cartPending: !!state.cartOperation,
        generation: state.generation
      };
    }
  };

  global.LumosCartOperations = api;
}(window));
